/**
 * PWA Branding: settings keys and validation limits.
 * Used by Settings form and by manifest/branding consumers.
 */
export const PWA_BRANDING_KEYS = {
  APP_NAME: 'pwaName',
  SHORT_NAME: 'pwaShortName',
  DESCRIPTION: 'pwaDescription',
  ICON: 'pwaIcon',
  THEME_COLOR: 'pwaThemeColor',
  BACKGROUND_COLOR: 'pwaBackgroundColor',
};

export const PWA_APP_NAME_MAX_LENGTH = 60;
export const PWA_SHORT_NAME_MAX_LENGTH = 20;
export const PWA_DESCRIPTION_MAX_LENGTH = 160;

export const PWA_THEME_COLOR_DEFAULT = '#000000';
export const PWA_BACKGROUND_COLOR_DEFAULT = '#ffffff';

export const PWA_ICON_RECOMMENDED_SIZES = [192, 512];

/** GET: retorna as URLs atuais do ícone PWA (público, sem company). */
export const getPwaIconApiPath = () => '/pwa/icon';

/** POST: envia novo arquivo (multipart); requer companyId do usuário logado. */
export const getPwaIconUploadUrl = (companyId) =>
  companyId != null && companyId !== '' ? `/companies/${companyId}/pwa/icon` : '/pwa/icon';

/** Retorna true se for URL absoluta (http/https). */
function isAbsoluteIconUrl(value) {
  if (typeof value !== 'string') return false;
  const t = value.trim();
  return t.length > 0 && (t.startsWith('http://') || t.startsWith('https://'));
}

/** Retorna true se for caminho relativo ou nome de arquivo (ex.: public/xxx.png). blob/data não são paths do backend. */
function isIconPath(value) {
  if (typeof value !== 'string') return false;
  const t = value.trim();
  if (t.startsWith('blob:') || t.startsWith('data:')) return false;
  return t.length > 0 && !t.startsWith('http://') && !t.startsWith('https://');
}

/**
 * Normaliza a resposta de GET/POST /companies/:id/pwa/icon.
 * Backend GET/POST /pwa/icon pode devolver no topo ou em data/result.
 */
export function normalizePwaIconResponse(data) {
  const empty = { iconUrl: '', appleTouchIconUrl: '', icon192Url: '', icon512Url: '' };
  if (!data || typeof data !== 'object') return empty;
  const hasIconKeys = (o) =>
    o && typeof o === 'object' && (o.pwaIcon192Url ?? o.pwaAppleTouchIconUrl ?? o.pwaIcon512Url);
  const obj = hasIconKeys(data)
    ? data
    : hasIconKeys(data.data)
      ? data.data
      : hasIconKeys(data.result)
        ? data.result
        : data;

  const pwaAppleTouchIconUrl =
    obj.pwaAppleTouchIconUrl ?? obj.appleTouchIcon ?? obj.appleTouchIconUrl;
  const pwaIcon192Url = obj.pwaIcon192Url ?? obj.icon192 ?? obj.icon192Url;
  const pwaIcon512Url = obj.pwaIcon512Url ?? obj.icon512 ?? obj.icon512Url;

  const appleTouchIconUrl =
    isAbsoluteIconUrl(pwaAppleTouchIconUrl) || isIconPath(pwaAppleTouchIconUrl)
      ? String(pwaAppleTouchIconUrl).trim()
      : '';
  const icon192Url =
    isAbsoluteIconUrl(pwaIcon192Url) || isIconPath(pwaIcon192Url)
      ? String(pwaIcon192Url).trim()
      : '';
  const icon512Url =
    isAbsoluteIconUrl(pwaIcon512Url) || isIconPath(pwaIcon512Url)
      ? String(pwaIcon512Url).trim()
      : '';
  const iconUrl = icon192Url || icon512Url || appleTouchIconUrl;

  const pwaIconVersion =
    typeof obj.pwaIconVersion === 'number'
      ? obj.pwaIconVersion
      : typeof obj.pwaIconVersion === 'string'
        ? parseInt(obj.pwaIconVersion, 10)
        : undefined;

  return {
    iconUrl,
    appleTouchIconUrl: appleTouchIconUrl || iconUrl,
    icon192Url,
    icon512Url,
    pwaIconVersion: Number.isFinite(pwaIconVersion) ? pwaIconVersion : undefined,
  };
}

/** Chave no localStorage para a última versão do PWA conhecida (ícone/config). Usada para avisar "desinstale e reinstale". */
export const PWA_CONFIG_VERSION_STORAGE_KEY = 'pwa-config-version';
